import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, unref as _unref, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, createElementBlock as _createElementBlock, renderList as _renderList, Fragment as _Fragment, createElementVNode as _createElementVNode, resolveComponent as _resolveComponent, vShow as _vShow, withDirectives as _withDirectives, withCtx as _withCtx } from "vue"

const _hoisted_1 = {
  class: "page-wrapper",
  "data-test": "page-wrapper"
}
const _hoisted_2 = {
  id: "resume-overview-main-content",
  tabindex: "-1"
}
const _hoisted_3 = {
  class: "content-frame-wrapper",
  "data-test": "resume-overview"
}
const _hoisted_4 = { class: "content-card-wrapper" }
const _hoisted_5 = {
  key: 1,
  class: "page-title",
  "aria-level": "1",
  "data-test": "page-title"
}
const _hoisted_6 = {
  key: 3,
  class: "description",
  "data-test": "description"
}
const _hoisted_7 = {
  class: "card-wrapper",
  "data-test": "card-wrapper"
}
const _hoisted_8 = { key: 4 }

import { computed, onMounted, ref } from "vue";
import { IonPage, IonContent } from "@ionic/vue";
import MobileHeader from "@/components/Common/MobileHeader/MobileHeader.vue";
import DesktopLocaleSelector from "@/components/Common/LocaleSelector/DesktopLocaleSelector.vue";
import Breadcrumbs from "@/components/Common/Breadcrumbs/Breadcrumbs.vue";
import AppButton from "@/components/Common/AppButton/AppButton.vue";
import AppModal from "@/components/Common/AppModal/AppModal.vue";
import ErrorModal from "@/components/Common/ErrorModal/ErrorModal.vue";
import ContentCard from "@/components/Resume/ContentCard/ContentCard.vue";
import { useI18n } from "vue-i18n";
import { call, business, ribbon, briefcase, person } from "ionicons/icons";
import type { Button } from "@/components/Common/AppButton/AppButton.vue";
import TitleSkeleton from "@/components/Skeletons/TitleSkeleton/TitleSkeleton.vue";
import { useResumeStore, useErrorStore } from "@/store";
import { useRouter, useRoute } from "vue-router";
import { checkmarkCircle } from "ionicons/icons";
import { focusOnMainContent } from "@/helpers";
import { onIonViewDidEnter } from "@ionic/vue";
import { reportResumeDownloaded } from "@/helpers/reporting";

interface CardContent {
  title: string;
  description: string;
  buttonText: string;
  buttonType: Button;
  icon: string;
  routeName: string;
}

export default /*@__PURE__*/_defineComponent({
  __name: 'ResumeOverview',
  setup(__props) {

const { t } = useI18n<{}>({
  useScope: "global",
});
const router = useRouter();
const route = useRoute();
const openDownloadModal = ref(false);
const openSuccessModal = ref(false);
const isDownloading = ref(false);

const resumeStore = useResumeStore();
const errorStore = useErrorStore();

const disableDownload = computed(() => {
  if (
    resumeStore.isDetailsCompleted ||
    resumeStore.isExperienceCompleted ||
    resumeStore.isQualificationsCompleted ||
    resumeStore.isLicencesCompleted ||
    resumeStore.isPersonalStatementCompleted
  )
    return false;
  else return true;
});

onMounted(async () => {
  await resumeStore.requestResume();
});

onIonViewDidEnter(() => {
  focusOnMainContent(route?.meta?.mainContentId);
});

const resumeCardContent = computed((): CardContent[] => {
  return [
    {
      title: "resume.resumeContentCard.contactDetails.title",
      description: "resume.resumeContentCard.contactDetails.description",
      buttonText: resumeStore.isDetailsCompleted
        ? "resume.resumeContentCard.contactDetails.editButton"
        : "resume.resumeContentCard.contactDetails.addButton",
      buttonType: resumeStore.isDetailsCompleted ? "tertiary" : "secondary",
      icon: call,
      routeName: "ResumeContactDetails",
    },
    {
      title: "resume.resumeContentCard.experience.title",
      description: "resume.resumeContentCard.experience.description",
      buttonText: resumeStore.isExperienceCompleted
        ? "resume.resumeContentCard.experience.editButton"
        : "resume.resumeContentCard.experience.addButton",
      buttonType: resumeStore.isExperienceCompleted ? "tertiary" : "secondary",
      icon: business,
      routeName: "ResumeWorkExperiences",
    },
    {
      title: "resume.resumeContentCard.qualifications.title",
      description: "resume.resumeContentCard.qualifications.description",
      buttonText: resumeStore.isQualificationsCompleted
        ? "resume.resumeContentCard.qualifications.editButton"
        : "resume.resumeContentCard.qualifications.addButton",
      buttonType: resumeStore.isQualificationsCompleted
        ? "tertiary"
        : "secondary",
      icon: ribbon,
      routeName: "ResumeQualifications",
    },
    {
      title: "resume.resumeContentCard.licences.title",
      description: "resume.resumeContentCard.licences.description",
      buttonText: resumeStore.isLicencesCompleted
        ? "resume.resumeContentCard.licences.editButton"
        : "resume.resumeContentCard.licences.addButton",
      buttonType: resumeStore.isLicencesCompleted ? "tertiary" : "secondary",
      icon: briefcase,
      routeName: "ResumeLicences",
    },
    {
      title: "resume.resumeContentCard.personalStatement.title",
      description: "resume.resumeContentCard.personalStatement.description",
      buttonText: resumeStore.isPersonalStatementCompleted
        ? "resume.resumeContentCard.personalStatement.editButton"
        : "resume.resumeContentCard.personalStatement.addButton",
      buttonType: resumeStore.isPersonalStatementCompleted
        ? "tertiary"
        : "secondary",
      icon: person,
      routeName: "ResumePersonalStatement",
    },
  ];
});

const handleClick = (routeName: string) => {
  router.push({ name: routeName });
};

const triggerDownload = () => {
  openDownloadModal.value = true;
};
const downloadResume = async () => {
  errorStore.setDownloadResumeError(false);
  isDownloading.value = true;

  if (resumeStore.resumeId) {
    reportResumeDownloaded(resumeStore.resumeId);
  }

  try {
    const filePrefix = t('resume.filePrefix');
    const downloadSuccess = await resumeStore.downloadResumeToDoc(filePrefix);
    if (downloadSuccess) {
      openSuccessModal.value = true;
    }
  } catch (error) {
    errorStore.setDownloadResumeError(true);
    console.error("Failed to download resume:", error);
  } finally {
    isDownloading.value = false;
    openDownloadModal.value = false;
  }
};
const retryDownload = () => {
  errorStore.setDownloadResumeError(false);
  triggerDownload();
};

return (_ctx: any,_cache: any) => {
  const _component_ion_skeleton_text = _resolveComponent("ion-skeleton-text")!

  return (_openBlock(), _createBlock(_unref(IonPage), null, {
    default: _withCtx(() => [
      _createVNode(MobileHeader),
      _createVNode(_unref(IonContent), null, {
        default: _withCtx(() => [
          _createVNode(DesktopLocaleSelector),
          _createElementVNode("div", _hoisted_1, [
            _createVNode(Breadcrumbs, {
              tab: "tools",
              doc: _unref(t)('resume.breadcrumbs.resume'),
              "data-test": "breadcrumbs"
            }, null, 8, ["doc"]),
            _createElementVNode("div", _hoisted_2, [
              _createElementVNode("div", _hoisted_3, [
                _createElementVNode("div", _hoisted_4, [
                  (_unref(resumeStore).isResumeLoading)
                    ? (_openBlock(), _createBlock(TitleSkeleton, {
                        key: 0,
                        class: "page-title",
                        "data-test": "title-skelton"
                      }))
                    : (_openBlock(), _createElementBlock("h1", _hoisted_5, _toDisplayString(_unref(t)("resume.overview.title")), 1)),
                  (_unref(resumeStore).isResumeLoading)
                    ? (_openBlock(), _createBlock(TitleSkeleton, {
                        key: 2,
                        class: "description",
                        style: {"width":"50%"},
                        "data-test": "description-skelton"
                      }))
                    : (_openBlock(), _createElementBlock("div", _hoisted_6, _toDisplayString(_unref(t)("resume.overview.description")), 1)),
                  _createElementVNode("div", _hoisted_7, [
                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(resumeCardContent.value, (card) => {
                      return (_openBlock(), _createBlock(ContentCard, {
                        key: card.routeName,
                        id: card.routeName,
                        title: card.title,
                        description: card.description,
                        buttonText: card.buttonText,
                        buttonType: card.buttonType,
                        icon: card.icon,
                        isLoading: _unref(resumeStore).isResumeLoading,
                        "data-test": "content-card",
                        onCardClicked: handleClick
                      }, null, 8, ["id", "title", "description", "buttonText", "buttonType", "icon", "isLoading"]))
                    }), 128))
                  ]),
                  (_unref(resumeStore).isResumeLoading)
                    ? (_openBlock(), _createElementBlock("div", _hoisted_8, [
                        _createVNode(_component_ion_skeleton_text, {
                          animated: true,
                          style: {"width":"312px","height":"44px"}
                        })
                      ]))
                    : (_openBlock(), _createBlock(AppButton, {
                        key: 5,
                        size: "default",
                        disabled: disableDownload.value,
                        type: "primary",
                        width: "312px",
                        label: _unref(t)('resume.overview.downloadMyResume'),
                        ariaLabel: _unref(t)('resume.overview.downloadMyResume'),
                        "data-test": "download-button",
                        onClick: triggerDownload
                      }, null, 8, ["disabled", "label", "ariaLabel"]))
                ])
              ])
            ]),
            (openDownloadModal.value)
              ? (_openBlock(), _createBlock(AppModal, {
                  key: 0,
                  isOpen: openDownloadModal.value,
                  title: _unref(t)('resume.downloadModal.title'),
                  body: _unref(t)('resume.downloadModal.body'),
                  primaryButton: {
            label: _unref(t)('resume.downloadModal.primaryButtonText'),
            isLoading: isDownloading.value,
          },
                  secondaryButton: {
            label: _unref(t)('resume.downloadModal.secondaryButtonText'),
          },
                  onOnPrimaryButtonClick: downloadResume,
                  onOnSecondaryButtonClick: _cache[0] || (_cache[0] = ($event: any) => (openDownloadModal.value = false)),
                  "data-test": "download-modal"
                }, null, 8, ["isOpen", "title", "body", "primaryButton", "secondaryButton"]))
              : _createCommentVNode("", true),
            (openSuccessModal.value)
              ? (_openBlock(), _createBlock(AppModal, {
                  key: 1,
                  isOpen: openSuccessModal.value,
                  title: _unref(t)('resume.downloadSuccessModal.title'),
                  body: '',
                  primaryButton: {
            label: _unref(t)('resume.downloadSuccessModal.primaryButtonText'),
          },
                  onOnPrimaryButtonClick: _cache[1] || (_cache[1] = ($event: any) => (openSuccessModal.value = false)),
                  icon: _unref(checkmarkCircle)
                }, null, 8, ["isOpen", "title", "primaryButton", "icon"]))
              : _createCommentVNode("", true),
            _withDirectives(_createVNode(ErrorModal, {
              hasErrored: _unref(errorStore).hasDownloadResumeError,
              onOnDismiss: _cache[2] || (_cache[2] = ($event: any) => (_unref(errorStore).setDownloadResumeError(false))),
              "data-test": "error-modal",
              class: "error-modal",
              title: _unref(t)('resume.downloadErrorModal.title'),
              body: _unref(t)('resume.downloadErrorModal.body'),
              primaryButton: {
            label: _unref(t)('resume.downloadErrorModal.primaryButtonText'),
          },
              secondaryButton: {
            label: _unref(t)('resume.downloadErrorModal.secondaryButtonText'),
          },
              onOnPrimaryButtonClick: retryDownload,
              onOnSecondaryButtonClick: _cache[3] || (_cache[3] = ($event: any) => (_unref(errorStore).setDownloadResumeError(false)))
            }, null, 8, ["hasErrored", "title", "body", "primaryButton", "secondaryButton"]), [
              [_vShow, _unref(errorStore).hasDownloadResumeError]
            ])
          ])
        ]),
        _: 1
      })
    ]),
    _: 1
  }))
}
}

})